import type { QuestDefinition, QuestInstance, QuestDifficulty } from '../types/quest';
import type { CompletionRecord } from '../types/progress';
import type { Subject } from '../types/activity';
import type { Child } from '../types/child';
import { seededRandom } from '../utils/random';

const SUBJECTS: Subject[] = ['literacy', 'math', 'science', 'social-studies', 'physical', 'creative', 'coding', 'financial', 'eq'];

const QUEST_TEMPLATES: Omit<QuestDefinition, 'params'>[] = [
  // Easy quests
  { type: 'complete-count', difficulty: 'easy', title: 'Getting Started', description: 'Complete 2 activities today', emoji: '📝', target: 2, coinReward: 10, xpReward: 15 },
  { type: 'perfect-activity', difficulty: 'easy', title: 'Star Student', description: 'Get 3 stars on any activity', emoji: '⭐', target: 1, coinReward: 10, xpReward: 15 },
  { type: 'streak-extend', difficulty: 'easy', title: 'Keep It Going', description: 'Extend your learning streak', emoji: '🔥', target: 1, coinReward: 10, xpReward: 15 },
  { type: 'time-goal', difficulty: 'easy', title: 'Quick Learner', description: 'Learn for 30 minutes today', emoji: '⏱️', target: 30, coinReward: 10, xpReward: 15 },

  // Medium quests
  { type: 'complete-count', difficulty: 'medium', title: 'Busy Bee', description: 'Complete 5 activities today', emoji: '🐝', target: 5, coinReward: 20, xpReward: 30 },
  { type: 'subject-stars', difficulty: 'medium', title: 'Subject Star', description: 'Get 3 stars in a specific subject', emoji: '🌟', target: 1, coinReward: 20, xpReward: 30 },
  { type: 'new-subject', difficulty: 'medium', title: 'Explorer', description: 'Try 3 different subjects today', emoji: '🧭', target: 3, coinReward: 20, xpReward: 30 },
  { type: 'time-goal', difficulty: 'medium', title: 'Study Session', description: 'Learn for 60 minutes today', emoji: '📚', target: 60, coinReward: 20, xpReward: 30 },
  { type: 'perfect-activity', difficulty: 'medium', title: 'Triple Star', description: 'Get 3 stars on 3 activities', emoji: '✨', target: 3, coinReward: 20, xpReward: 30 },

  // Hard quests
  { type: 'complete-count', difficulty: 'hard', title: 'Superstar Scholar', description: 'Complete 8 activities today', emoji: '🚀', target: 8, coinReward: 40, xpReward: 50 },
  { type: 'new-subject', difficulty: 'hard', title: 'Renaissance Kid', description: 'Try 5 different subjects today', emoji: '🎭', target: 5, coinReward: 40, xpReward: 50 },
  { type: 'perfect-activity', difficulty: 'hard', title: 'Perfect Day', description: 'Get 3 stars on 5 activities', emoji: '💎', target: 5, coinReward: 40, xpReward: 50 },
  { type: 'time-goal', difficulty: 'hard', title: 'Marathon Learner', description: 'Learn for 120 minutes today', emoji: '🏆', target: 120, coinReward: 40, xpReward: 50 },
];

function pickQuestForDifficulty(difficulty: QuestDifficulty, rng: () => number): QuestDefinition {
  const pool = QUEST_TEMPLATES.filter(q => q.difficulty === difficulty);
  const idx = Math.floor(rng() * pool.length);
  const template = pool[idx];

  const params: QuestDefinition['params'] = {};
  if (template.type === 'subject-stars') {
    params.subject = SUBJECTS[Math.floor(rng() * SUBJECTS.length)];
  }

  return { ...template, params: Object.keys(params).length > 0 ? params : undefined };
}

export function generateDailyQuests(childId: string, date: string): QuestInstance[] {
  const rng = seededRandom(`quests-${date}-${childId}`);

  const difficulties: QuestDifficulty[] = ['easy', 'medium', 'hard'];
  return difficulties.map((diff, i) => {
    const definition = pickQuestForDifficulty(diff, rng);
    return {
      id: `quest-${date}-${childId}-${i}`,
      childId,
      date,
      definition,
      progress: 0,
      completed: false,
      claimed: false,
    };
  });
}

export function evaluateQuestProgress(
  quest: QuestInstance,
  todayCompletions: CompletionRecord[],
  child: Child,
): number {
  const childCompletions = todayCompletions.filter(c => c.childId === child.id);

  switch (quest.definition.type) {
    case 'complete-count':
      return childCompletions.length;

    case 'perfect-activity':
      return childCompletions.filter(c => c.starsEarned === 3).length;

    case 'subject-stars': {
      const subj = quest.definition.params?.subject;
      if (!subj) return 0;
      return childCompletions.filter(c => c.subject === subj && c.starsEarned === 3).length;
    }

    case 'new-subject': {
      const uniqueSubjects = new Set(childCompletions.map(c => c.subject));
      return uniqueSubjects.size;
    }

    case 'streak-extend':
      return child.currentStreak > 0 ? 1 : 0;

    case 'time-goal':
      return childCompletions.reduce((sum, c) => sum + c.durationMinutes, 0);

    case 'project-day':
      return childCompletions.filter(c => c.activityId.startsWith('proj-')).length;

    case 'skill-practice':
      return childCompletions.length;

    default:
      return 0;
  }
}

export function isQuestComplete(quest: QuestInstance, progress: number): boolean {
  return progress >= quest.definition.target;
}
